import React from 'react';

import CrossIcon from 'icons/ic-close.svg';
import TickIcon from 'icons/ic-tick.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import { useCurrentActor } from 'components/contexts/CurrentActor';

export default function ArchiveModal({
  closeModal,
  hasELearningBookings,
  isArchived,
  isOpen,
  longName,
  onArchive,
  onUnarchive,
  shortName,
  unarchiveBody,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.ArchiveModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={closeModal}
            size='md'
          >
            {i18nTShared('cancel')}
          </TextButton>
          <FilledButton
            color={isArchived ? 'mint' : 'red'}
            onClick={isArchived ? onUnarchive : onArchive}
          >
            {i18nTShared(isArchived ? 'unarchiveName' : 'archiveName', {
              interpolation: { escapeValue: false },
              name: shortName,
            })}
          </FilledButton>
        </div>
      }
      isOpen={isOpen}
      mode='flexible'
      onClose={closeModal}
    >
      <div className='tw-p-8'>
        <h2 className='tw-m-0 tw-p-0 tw-text-l tw-font-semibold tw-text-grey-900'>
          {i18nT(`${isArchived ? 'unarchive' : 'archive'}.title`, {
            interpolation: { escapeValue: false },
            name: longName,
          })}
        </h2>
        {isArchived ?
          unarchiveBody
        : <div className='tw-mt-4'>
            <p className='tw-mb-2'>{i18nT('archive.body.label')}</p>
            <ul className='app-ul'>
              {!currentActor.isAllowedFeature('training_register_only') && (
                <li className='tw-flex'>
                  <CrossIcon
                    className='[&_polygon]:tw-fill-red-600'
                    height={20}
                    width={20}
                  />
                  <span>
                    {i18nT('archive.body.cantBeAddedToProjects', {
                      interpolation: { escapeValue: false },
                      pluralRAMSName:
                        currentActor.division.attributes.pluralRAMSName,
                    })}
                  </span>
                </li>
              )}
              <li className='tw-flex'>
                <CrossIcon
                  className='[&_polygon]:tw-fill-red-600'
                  height={20}
                  width={20}
                />
                <span>
                  {i18nT('archive.body.noExpiringTrainingNotifications')}
                </span>
              </li>
              <li className='tw-flex'>
                <TickIcon
                  className='[&_polygon]:tw-fill-green-500'
                  height={20}
                  width={20}
                />
                <span>{i18nT('archive.body.canViewProfileAndRestore')}</span>
              </li>
            </ul>
            {hasELearningBookings && (
              <p className='tw-m-0 tw-mt-5 tw-rounded-lg tw-border-0 tw-bg-red-025 tw-p-3 tw-text-red-800'>
                {i18nT('archive.body.eLearningBookings')}
              </p>
            )}
          </div>
        }
      </div>
    </ModalBox>
  );
}

ArchiveModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  hasELearningBookings: PropTypes.bool,
  isArchived: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  longName: PropTypes.string.isRequired,
  onArchive: PropTypes.func.isRequired,
  onUnarchive: PropTypes.func.isRequired,
  shortName: PropTypes.string.isRequired,
  unarchiveBody: PropTypes.object,
};

ArchiveModal.defaultProps = {
  unarchiveBody: null,
};
